import React, { useEffect } from 'react'
import { createRootRoute, HeadContent, Outlet, Scripts, useRouter } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from '@/components/theme-provider'
import { CurrencyProvider } from '@/hooks/useCurrency'
import { Toaster } from '@/components/ui/sonner'
import ErrorBoundary from '@/components/ErrorBoundary'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary'
import { AuthProvider } from '@/contexts/AuthContext'
import { SettingsProvider } from '@/contexts/SettingsContext'
import { RootConfigContext } from '@/contexts/RootConfigContext'
import { useReferralTracker } from '@/hooks/useReferralTracker'
import { useAuth } from '@/contexts/AuthContext'
import { accountQueries } from '@/services/accountApi'
import { useQuery } from '@tanstack/react-query'
import { useLang } from '@/hooks/useLang'
import { getEnvConfig } from '@/lib/env'
import { generateSeo } from '@/lib/seo'
import { initSentry } from '@/lib/sentry'
import '@/lib/i18n'
import '@/styles/app.css'
import flagIconsUrl from 'flag-icons/css/flag-icons.min.css?url'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
})

// Loader return type
interface RootLoaderData {
  googleClientId: string
  apiBaseUrl: string
  stripePublishableKey: string
  umamiWebsiteId: string | undefined
  sentryDsn: string | undefined
}

export const Route = createRootRoute({
  component: RootShell,
  errorComponent: RouteErrorBoundary,
  loader: async (): Promise<RootLoaderData> => {
    return getEnvConfig()
  },
  head: ({ matches }) => {
    const safeMatches = matches || []

    // Find all staticData and merge them from root to leaf
    const staticMeta = safeMatches.reduce((acc, route) => {
      const data = (route.staticData as any) || {}
      return {
        ...acc,
        ...data,
      }
    }, {} as any)

    // Find the most specific route that has staticData or is a leaf
    const activeRoute = [...safeMatches].reverse().find((route) => route.routeId !== "__root__")
    const pathname = activeRoute?.pathname || '/'

    // If a route has its own head function (like blog or rates),
    // it will override this root head.
    // However, for static routes, we need this.

    const { meta, links } = generateSeo({
      title: staticMeta.title,
      description: staticMeta.description,
      keywords: staticMeta.keywords,
      pathname: pathname,
    })

    return {
      meta: [
        { charSet: 'utf-8' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...meta,
      ],
      links: [
        { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
        { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
        // Blocking stylesheet: only 1.5 KB, and font-display=swap in the
        // response means text renders immediately in the fallback font while
        // the actual font files load in the background.
        {
          rel: 'stylesheet',
          href: 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Plus+Jakarta+Sans:wght@500;600;700;800&family=EB+Garamond:wght@400;500;600&family=Carter+One&display=swap',
        },
        ...links,
      ],
      scripts: [],
    }
  },
})

function UmamiIdentify() {
  const { isAuthenticated } = useAuth()
  const { data } = useQuery({ ...accountQueries.me(), enabled: isAuthenticated })
  const account = data?.account

  useEffect(() => {
    if (account) {
      window.umami?.identify(account.id, { email: account.email })
    }
  }, [account])

  return null
}

// Loads a stylesheet non-blocking. The link element is created entirely in JS
// (not rendered by React/SSR) so there's nothing in the server HTML to block
// rendering. The load listener is attached directly on the DOM node.
function DeferredStylesheet({ href }: { href: string }) {
  useEffect(() => {
    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'style'
    link.href = href
    link.addEventListener('load', () => { link.rel = 'stylesheet' }, { once: true })
    document.head.appendChild(link)
    return () => { document.head.removeChild(link) }
  }, [href])

  return null
}

function RootShell() {
  const { googleClientId, apiBaseUrl, stripePublishableKey, umamiWebsiteId, sentryDsn } = Route.useLoaderData()

  // Initialize Sentry as early as possible
  useEffect(() => {
    initSentry(sentryDsn)
  }, [sentryDsn])

  const rootConfig = {
    googleClientId,
    apiBaseUrl,
    stripePublishableKey,
  }

  useReferralTracker()
  const lang = useLang()
  const router = useRouter()

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  useEffect(() => {
    const trackPageView = (url: string) => {
      if (url.startsWith('/admin')) return
      window.umami?.track(props => ({ ...props, url }))
    }

    trackPageView(router.state.location.pathname)

    return router.subscribe('onResolved', ({ toLocation }) => {
      trackPageView(toLocation.pathname)
    })
  }, [router])

  return (
    <html lang={lang}>
      <head>
        {/*
          Blocking script: reads the stored theme from localStorage and applies
          the correct class to <html> before the first paint, preventing a flash
          of the wrong theme on page load.

          This must run synchronously (no defer/async) and before <HeadContent />
          so it executes before any CSS or content is rendered.

          Keep this in sync with ThemeProvider (storageKey, theme values).
        */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function () {
                try {
                  var stored = localStorage.getItem('phonesalad-ui-theme');
                  var prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                  var theme = stored === 'dark' || stored === 'light'
                    ? stored
                    : prefersDark ? 'dark' : 'light';
                  document.documentElement.classList.add(theme);
                } catch (e) {
                  // localStorage may be unavailable (e.g. private browsing, sandboxed iframe)
                  // Fall back to light theme silently
                }
              })();
            `,
          }}
        />
        {umamiWebsiteId && (
          <script
            src="https://analytics.bitflo.io/script.js"
            defer
            data-website-id={umamiWebsiteId}
            data-auto-track="false"
          />
        )}
        <HeadContent />
        {/*
          Non-blocking flag icons (412 KB): fetched at high priority without
          blocking rendering, then applied once downloaded.
        */}
        <DeferredStylesheet href={flagIconsUrl} />
      </head>
      <body>
        <React.StrictMode>
          <ErrorBoundary>
            <QueryClientProvider client={queryClient}>
              <AuthProvider>
                <CurrencyProvider>
                  <ThemeProvider defaultTheme="system" storageKey="phonesalad-ui-theme">
                    <SettingsProvider>
                      <GoogleOAuthProvider clientId={googleClientId}>
                        <RootConfigContext.Provider value={rootConfig}>
                          <UmamiIdentify />
                          <Outlet />
                          <Toaster position="bottom-right" />
                        </RootConfigContext.Provider>
                      </GoogleOAuthProvider>
                    </SettingsProvider>
                  </ThemeProvider>
                </CurrencyProvider>
              </AuthProvider>
            </QueryClientProvider>
          </ErrorBoundary>
        </React.StrictMode>
        <Scripts />
      </body>
    </html>
  )
}
